import PropTypes from "prop-types";
import styled from "styled-components";
import { FaPrint, FaShare } from "react-icons/fa";
import ContentLayout from "../../../layout/ContentLayout";
import { H2, H3, Paragraph } from "../../../styles/AppStyles";
import { LINKS, href } from "../../../config/links";

const CalloutBox = styled.div`
  margin: 1.5rem 0;
  padding: 1.25rem;
  border-left: 5px solid ${({ $type, theme }) => {
    if ($type === "do") return "#10b981";
    if ($type === "dont") return "#ef4444";
    return theme.iconColor;
  }};
  background-color: ${({ $type, theme }) => {
    if ($type === "do") return "rgba(16, 185, 129, 0.08)";
    if ($type === "dont") return "rgba(239, 68, 68, 0.08)";
    return "rgba(99, 102, 241, 0.08)";
  }};
  border-radius: 6px;
  color: ${({ theme }) => theme.text};

  strong {
    display: block;
    margin-bottom: 0.5rem;
    font-size: 1.1rem;
    color: ${({ $type }) => {
      if ($type === "do") return "#10b981";
      if ($type === "dont") return "#ef4444";
      return "#6366f1";
    }};
  }

  ul {
    margin: 0.75rem 0 0 0;
    padding-left: 1.5rem;

    li {
      margin-bottom: 0.5rem;
      line-height: 1.6;
    }
  }
`;

const ChecklistSection = styled.div`
  margin: 1.5rem 0;
  padding: 1.25rem;
  background-color: ${({ theme }) => theme.background};
  border: 1px solid ${({ theme }) => theme.iconColor};
  border-radius: 6px;
  color: ${({ theme }) => theme.text};

  h3 {
    margin-top: 0;
  }
`;

const ChecklistItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 0.75rem;

  input[type="checkbox"] {
    margin-top: 0.35rem;
    flex-shrink: 0;
    width: 1.1rem;
    height: 1.1rem;
    cursor: pointer;
  }

  label {
    flex: 1;
    cursor: pointer;
    line-height: 1.6;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 1rem;
  margin: 2rem 0;
  flex-wrap: wrap;

  @media (max-width: 640px) {
    flex-direction: column;

    button,
    a {
      width: 100%;
    }
  }
`;

const Button = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.875rem 1.5rem;
  background-color: ${({ theme }) => theme.iconColor};
  color: ${({ theme }) => theme.background};
  text-decoration: none;
  border-radius: 6px;
  font-weight: 600;
  font-size: 0.95rem;
  transition: all 0.3s ease;
  cursor: pointer;
  border: none;

  &:hover {
    opacity: 0.9;
    transform: translateY(-2px);
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const PhishingDetectionGuide = ({ istouch, isdarkmode }) => {
  const canonicalUrl = href(LINKS.QUICK_GUIDES.PHISHING_DETECTION);

  // const heroImageSrc = "/images/guides/phishing-detection.webp";

  return (
    <ContentLayout
      istouch={istouch}
      isdarkmode={isdarkmode}
      displayTitle="What to Do If You Suspect Phishing"
      seoTitle="Phishing Detection Guide: Recognize & Report | CSFSafe"
      canonicalUrl={canonicalUrl}
      description="Quick guide to recognize phishing emails and attacks, take immediate action, and report suspicious activity to protect your organization."
      ogTitle="Phishing Detection Guide: Recognize & Report | CSFSafe"
      ogDescription="Learn to spot phishing emails, take immediate action, and report threats. Practical steps for SMBs."
      // ogImage={heroImageSrc}
      // heroImageSrc={heroImageSrc}
      heroImageAlt="Phishing detection and email security"
      publishedLabel="Quick Reference Guide — July 21, 2026"
      showCTA={true}
    >
      <Paragraph>
        Phishing is one of the most common entry points for attackers. This
        quick guide helps you recognize phishing attempts, understand what to do
        immediately, and report the threat so your organization can protect
        others.
      </Paragraph>

      <H2>Recognizing Phishing</H2>

      <H3>Common Phishing Red Flags</H3>
      <Paragraph>
        Phishing emails try to trick you into revealing information or clicking
        a malicious link. Watch for these warning signs:
      </Paragraph>

      <CalloutBox>
        <strong>Red Flag Warning Signs</strong>
        <ul>
          <li>
            <strong>Suspicious sender address:</strong> Email appears to be from
            a trusted company but the domain looks slightly off (e.g.,
            "amaZon.com" instead of "amazon.com")
          </li>
          <li>
            <strong>Generic greeting:</strong> "Dear User" or "Dear Customer"
            instead of your actual name
          </li>
          <li>
            <strong>Urgent language:</strong> "Click now," "Verify immediately,"
            "Account will be closed"
          </li>
          <li>
            <strong>Request for sensitive information:</strong> Legitimate
            companies never ask for passwords, credit cards, or SSN via email
          </li>
          <li>
            <strong>Mismatched links:</strong> Hover over a link—the destination
            URL doesn't match the text shown
          </li>
          <li>
            <strong>Poor grammar or formatting:</strong> Spelling errors, odd
            spacing, or unprofessional appearance
          </li>
          <li>
            <strong>Suspicious attachments:</strong> Unexpected files,
            especially .exe, .zip, or macro-enabled documents
          </li>
          <li>
            <strong>Requests to disable security:</strong> "Disable your
            antivirus," "Turn off your email filter"
          </li>
        </ul>
      </CalloutBox>

      <H3>Specific Attack Types to Know</H3>

      <Paragraph>
        <strong>Business Email Compromise (BEC):</strong> Attacker impersonates a
        trusted executive or vendor, often requesting wire transfers or urgent
        action. These emails look legitimate because they're well researched.
      </Paragraph>

      <Paragraph>
        <strong>Credential Harvesting:</strong> Fake login pages or emails that
        trick you into entering your username and password on a fraudulent site.
      </Paragraph>

      <Paragraph>
        <strong>Malware Delivery:</strong> Emails with infected attachments or
        links to malicious websites designed to install malware on your device.
      </Paragraph>

      <H2>What to Do Right Now</H2>

      <ChecklistSection>
        <h3>Immediate Action Checklist</h3>
        <ChecklistItem>
          <input type="checkbox" id="item1" />
          <label htmlFor="item1">
            <strong>STOP:</strong> Don't click any links or download attachments
          </label>
        </ChecklistItem>
        <ChecklistItem>
          <input type="checkbox" id="item2" />
          <label htmlFor="item2">
            <strong>VERIFY:</strong> Contact the sender through a known phone
            number or official website (not via the email) to confirm the email
            is legitimate
          </label>
        </ChecklistItem>
        <ChecklistItem>
          <input type="checkbox" id="item3" />
          <label htmlFor="item3">
            <strong>ISOLATE:</strong> If you clicked a link or opened an
            attachment, disconnect your device from the network immediately
          </label>
        </ChecklistItem>
        <ChecklistItem>
          <input type="checkbox" id="item4" />
          <label htmlFor="item4">
            <strong>REPORT:</strong> Forward the email to your IT security team
            or phishing report address (ask your team for the address)
          </label>
        </ChecklistItem>
        <ChecklistItem>
          <input type="checkbox" id="item5" />
          <label htmlFor="item5">
            <strong>DELETE:</strong> Delete the suspicious email (don't reply)
          </label>
        </ChecklistItem>
        <ChecklistItem>
          <input type="checkbox" id="item6" />
          <label htmlFor="item6">
            <strong>MONITOR:</strong> Watch your accounts for unusual activity
            over the next 30 days
          </label>
        </ChecklistItem>
      </ChecklistSection>

      <H3>If You Think You've Been Compromised</H3>

      <CalloutBox $type="alert">
        <strong>Act Within the First Hour</strong>
        <ul>
          <li>Change your password immediately from a clean device</li>
          <li>
            Enable or strengthen two-factor authentication (2FA) on important
            accounts
          </li>
          <li>
            Alert your IT team so they can monitor your account for lateral
            movement
          </li>
          <li>
            Check your recent account activity for logins from unfamiliar
            locations or times
          </li>
          <li>
            Consider a credit freeze if personal or financial information was
            involved
          </li>
        </ul>
      </CalloutBox>

      <H2>Reporting the Threat</H2>

      <H3>Who to Tell</H3>

      <Paragraph>
        <strong>Internal Reporting:</strong> Most organizations have a security
        team or IT helpdesk. Forward the suspicious email to them. Include the
        email headers if possible (usually available in "Show original" or
        "Message details").
      </Paragraph>

      <Paragraph>
        <strong>External Reporting:</strong> You can also report phishing to:
      </Paragraph>
      <ul>
        <li>
          The company being impersonated (find their security reporting page on
          their official website)
        </li>
        <li>Your email provider (Gmail, Outlook, etc.)</li>
        <li>
          The FBI's IC3 (ic3.gov) for financial loss or serious threats
        </li>
        <li>CISA (cisa.gov) for cyber threats to critical infrastructure</li>
      </ul>

      <H3>What Information to Include</H3>

      <CalloutBox $type="tip">
        <strong>When Reporting Phishing</strong>
        <ul>
          <li>Full email headers (shows routing and sender details)</li>
          <li>Sender email address</li>
          <li>Subject line</li>
          <li>Any URLs or attachments in the email</li>
          <li>When you received it</li>
          <li>Whether anyone in your organization clicked it</li>
        </ul>
      </CalloutBox>

      <H2>Prevention: Do's and Don'ts</H2>

      <CalloutBox $type="do">
        <strong>DO These Things</strong>
        <ul>
          <li>
            Hover over links to see the actual URL before clicking (don't rely
            on the link text)
          </li>
          <li>
            Check sender email addresses carefully—slight misspellings are
            common
          </li>
          <li>
            Contact companies directly if you're unsure—use the phone number or
            website on your account
          </li>
          <li>
            Use a password manager so you're less likely to enter credentials on
            a fake site
          </li>
          <li>Enable two-factor authentication on sensitive accounts</li>
          <li>Keep your email client and browser updated</li>
          <li>
            Think before you act—legitimate companies don't demand urgent action
            via email
          </li>
        </ul>
      </CalloutBox>

      <CalloutBox $type="dont">
        <strong>DON'T Do These Things</strong>
        <ul>
          <li>
            Click links in emails from unknown senders or even if they appear to
            be from trusted sources
          </li>
          <li>Download attachments you weren't expecting</li>
          <li>
            Provide passwords, PINs, or personal information via email—legitimate
            companies never ask for this
          </li>
          <li>Trust the "From" address alone—it can be spoofed</li>
          <li>
            Disable security software or email filters, even if an email tells
            you to
          </li>
          <li>Wire money or purchase gift cards on demand via email</li>
          <li>Assume an email is safe just because it looks professional</li>
        </ul>
      </CalloutBox>

      <H2>Create a Culture of Reporting</H2>

      <Paragraph>
        The strongest defense against phishing is a team that reports suspicious
        emails. Make sure everyone in your organization knows:
      </Paragraph>
      <ul>
        <li>
          <strong>How to report:</strong> Where to send suspected phishing (email
          address, button in email client, help desk ticket, etc.)
        </li>
        <li>
          <strong>They won't be punished:</strong> Reporting a mistake is better
          than remaining silent
        </li>
        <li>
          <strong>Reporting saves time:</strong> One report stops a threat for
          everyone
        </li>
        <li>
          <strong>Training matters:</strong> Regular phishing simulations help
          people stay alert
        </li>
      </ul>

      <H2>Related Resources</H2>
      <ul>
        <li>
          <a href={href(LINKS.THREAT_TYPE.PHISHING_THREAT)}>
            Phishing Threat Types: Full Article
          </a>
        </li>
        <li>
          <a href={href(LINKS.THREAT_TYPE.BUSINESS_EMAIL_COMPROMISE)}>
            Business Email Compromise (BEC)
          </a>
        </li>
        <li>
          <a href={href(LINKS.BEST_PRACTICES.ACCESS_CONTROL)}>
            Access Control &amp; Password Basics
          </a>
        </li>
      </ul>

      <ButtonRow data-print="hide">
        <Button
          as="button"
          type="button"
          onClick={() => window.print()}
          style={{ fontFamily: "inherit" }}
        >
          <FaPrint /> Print or save as PDF
        </Button>
        <Button
          href="https://www.facebook.com/sharer/sharer.php?u=https://csfsafe.com/resources/quick-guides/phishing-detection"
          target="_blank"
          rel="noopener noreferrer"
        >
          <FaShare /> Share Guide
        </Button>
      </ButtonRow>
    </ContentLayout>
  );
};

PhishingDetectionGuide.propTypes = {
  istouch: PropTypes.bool.isRequired,
  isdarkmode: PropTypes.bool.isRequired,
};

export default PhishingDetectionGuide;
